import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { InfluencerDetailsService } from '../../services/influencer-details.service';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { InfluencerDetails } from '../../models/influencerdetails.model';
import { ApiResponse } from '../../../../shared/models/api-response.model';
import { HttpErrorResponse } from '@angular/common/http';
import { getApiErrorMessage } from '../../../../shared/utils/error-utils';
import { Paths } from '../../../../core/constants/path.constants';
import { PollRes } from '../../../influencer/modules/poll/models/poll.inteface';
import { MediaResponse } from '../../../influencer/modules/media/models/media.model';
import { LocalStorageService } from '../../../../core/services/local-storage.service';
import { AuthResponse } from '../../../auth/models/auth.model';
import { LocalStorageKeys } from '../../../../core/constants/local-storage.constants';

@Component({
  selector: 'vota-influencer-details',
  templateUrl: './influencer-details.component.html',
  styleUrl: './influencer-details.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class InfluencerDetailsComponent implements OnInit, OnDestroy {
  isLoading = false;
  influencerDetails$: Observable<InfluencerDetails | null> = of(null);
  influencerPosts$: Observable<MediaResponse[] | null> = of([]);
  userData: InfluencerDetails | undefined;
  username: string | null = '';
  influencersPolls: PollRes[] | undefined;
  influencerLatestPoll: PollRes | null = null;
  userDetails: AuthResponse | undefined | null;
  preview: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private influencerDetailsService: InfluencerDetailsService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.username = this.route.snapshot.paramMap.get('username');
    this.getInfluencerDetails();
  }

  onNavigateToHome(): void {
    this.router.navigate([Paths.EMPTY]);
  }

  getInfluencerDetails(): void {
    if (this.username) {
      this.isLoading = true;

      this.userDetails =
        this.localStorageService.getItemWithExpiry<AuthResponse | null>(
          LocalStorageKeys.USER_DETAILS
        );
      const postsObservable = this.userDetails
        ? this.influencerDetailsService.getInfluencerSubscribedPosts({
            userName: this.username,
            pageNumber: 1,
          })
        : this.influencerDetailsService.getInfluencersPosts({
            userName: this.username,
            pageNumber: 1,
          });
      forkJoin({
        details: this.influencerDetailsService
          .getInfluencerDetails(this.username)
          .pipe(
            catchError((error: HttpErrorResponse) => {
              this.toastr.error(
                'Failed to load details: ' + getApiErrorMessage(error)
              );
              return of(null);
            })
          ),
        posts: postsObservable.pipe(
          map((postRes) => postRes?.data?.data ?? []),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(
              'Failed to load posts: ' + getApiErrorMessage(error)
            );
            return of([]);
          })
        ),
      })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.isLoading = false))
        )
        .subscribe({
          next: ({ details, posts }) => {
            this.userData = details?.data ?? undefined;

            if (!details?.data) {
              this.influencerDetails$ = of(null);
              return;
            }
            const profileData = this.getInfluencerProfileData(details.data);
            this.influencerLatestPoll = profileData?.poll ?? null;
            this.influencerDetails$ = of(profileData);
            const safePosts: MediaResponse[] = posts || [];
            this.influencerPosts$ = of(safePosts);
            this.preview = safePosts.length > 0;
          },
        });
    }
  }

  onBackToHome(): void {
    this.router.navigate([Paths.EMPTY]);
  }

  onNavigateToInfluencerMedia(): void {
    this.router.navigate([Paths.EMPTY, this.username, 'media']);
  }

  onCastVote(value: { pollId: number; pollOptionId: number }): void {
    if (value) {
      this.influencerDetailsService
        .createVote(value)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.isLoading = false;
            this.getInfluencerDetails();
          })
        )
        .subscribe({
          next: (pollsResult: ApiResponse<null>) => {},
          error: (error: HttpErrorResponse) => {
            console.error(error);
            this.toastr.error(getApiErrorMessage(error));
          },
        });
    }
  }

  private getInfluencerProfileData(
    profileData: InfluencerDetails | null
  ): InfluencerDetails {
    return {
      userId: profileData?.userId ?? 0,
      firstName: profileData?.firstName
        ? profileData.firstName
        : this.username
        ? this.username
        : '',
      lastName: profileData?.lastName ? profileData.lastName : '',
      profileBio: profileData?.profileBio ? profileData.profileBio : '',
      profileImageUrl: profileData?.profileImageUrl
        ? profileData.profileImageUrl
        : 'assets/images/no-image.jpg',
      poll: profileData?.poll ? profileData?.poll : null,
      subscriptions: profileData?.subscriptions
        ? profileData?.subscriptions
        : [],
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
